import React, { useMemo } from "react";
import Script from "next/script";

import { ProductDetailsType } from "modules/product-details/types";
import { toTitleCase } from "common";

// ===================================================
type Props = { productDetails: ProductDetailsType; productImageLink?: string };
// ===================================================

const ProductJSONLDSchemaOrgTags: React.FC<Props> = ({
  productDetails,
  productImageLink,
}) => {
  const title = useMemo(() => {
    const { product_name } = productDetails;
    return product_name ? product_name.toUpperCase() : "";
  }, [productDetails]);

  const description = useMemo(() => {
    if (productDetails?.description) {
      return productDetails?.description;
    }
    if (productDetails?.product_name) {
      return toTitleCase(productDetails?.product_name);
    }
    return "";
  }, [productDetails]);

  const brand = useMemo(() => {
    return productDetails?.manufacturer;
  }, [productDetails]);

  const availability = useMemo(() => {
    if (productDetails?.status?.code === 1) {
      return "InStock";
    }
    return "OutOfStock";
  }, [productDetails?.status?.code]);

  const amount = useMemo(() => {
    let newAmount = productDetails?.retail_price?.toString();

    if (isNaN(parseFloat(newAmount))) {
      // Meaning a not valid amount
      newAmount = "";
    }

    return newAmount;
  }, [productDetails?.retail_price]);

  const finalObjString = useMemo(() => {
    // We can also add
    /**
     additionalProperty: [
        {
          "@type": "PropertyValue",
          propertyID: "item_group_id",
          value: "fb_tshirts",
        },
      ],
     */

    const obj = {
      "@context": "https://schema.org",
      "@type": "Product",
      productID: productDetails?.product_uuid,
      name: title,
      description: description,
      url: window.location.href,
      image: productImageLink,
      brand: brand,
      offers: [
        {
          "@type": "Offer",
          price: amount,
          priceCurrency: "PHP",
          itemCondition: "https://schema.org/NewCondition",
          availability: `https://schema.org/${availability}`,
        },
      ],
    };

    return JSON.stringify(obj);
  }, [
    amount,
    availability,
    productDetails?.product_uuid,
    productImageLink,
    title,
    description,
    brand
  ]);

  return (
    <div>
      <Script id="application/ld+json" type="application/ld+json">
        {finalObjString}
      </Script>
    </div>
  );
};

export default ProductJSONLDSchemaOrgTags;
